import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import { DatesCalculator } from "@shared/mirage/helpers/DatesCalculator";
import { memo, useCallback, useEffect, useRef } from "react";
import { atom, useRecoilState } from "recoil";
import ApplicationService from "@mirage/services/ApplicationService";
import { SessionManager } from "@mirage/manager/SessionManager";
import { useNavigate } from "react-router-dom";
import { MirageRoutingService } from "@mirage/services/MirageRoutingService";
import DateFormatter from "@shared/mirage/helpers/DateFormatter";
import { useLocalStorage } from "@mirage/hooks/useLocalStorage";
export interface SessionTimeoutProps {
    timeoutIn: number;
    open: boolean;
}

export const sessionTimeoutState = atom({
    key: "sessionTimeoutState",
    default: { open: false, timeoutIn: 0 } as SessionTimeoutProps,
});
const router = new MirageRoutingService();

const SessionTimeout = memo(() => {
    const navigate = useNavigate();
    const [state, setState] = useRecoilState(sessionTimeoutState);
    const deserializer = useCallback((value: string) => JSON.parse(value), []);
    const setSessionTimeout = useCallback(
        (props: Omit<SessionTimeoutProps, "open">) => {
            setState((prev) => {
                return {
                    ...prev,
                    open: true,
                    timeoutIn: props.timeoutIn,
                };
            });
        },
        [setState]
    );
    const service = new ApplicationService({ logoutNavigate: () => navigate(router.LoginRoute()), setSessionTimeout });
    const close = () => {
        setState((p) => {
            return { ...p, open: false };
        });
    };
    const [lastUserRequestTime] = useLocalStorage<Date | null>("lastUserRequestTime", null, { deserializer });
    const timeoutIntervalId = useRef<NodeJS.Timer>();

    useEffect(() => {
        timeoutIntervalId.current = setInterval(() => {
            setState((p) => {
                if (p.timeoutIn === 0) {
                    return p;
                }
                const newVal = p.timeoutIn - 1000;
                return { ...p, timeoutIn: newVal };
            });
        }, 1000);
        return () => {
            setState({ open: false, timeoutIn: 0 });
            clearInterval(timeoutIntervalId.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const routineWasPotentiallyProlongedInAnotherTab = !lastUserRequestTime;
        close();
        if (routineWasPotentiallyProlongedInAnotherTab) {
            SessionManager.clearSessionTimeouts();
            service.prolongRoutine();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastUserRequestTime]);

    const prolongSession = async () => {
        const newDate = new Date();
        SessionManager.lastUserRequestTime = newDate;
        const { errorMsg } = await service.refreshSession({ newDate, timeZone: DateFormatter.currentTimezone });
        if (errorMsg) {
            SessionManager.clearSessionTimeouts();
            service.logout();
        } else {
            service.prolongRoutine();
            close();
        }
    };

    return (
        <Dialog open={state.open}>
            <DialogTitle>Your session is going to timeout</DialogTitle>
            <DialogContent dividers>Time remaining: {DatesCalculator.convertSecondsToMinutesSeconds(state.timeoutIn)}</DialogContent>
            <DialogActions>
                <Button onClick={close}>Close</Button>
                <Button onClick={prolongSession} variant="contained">
                    Prolong Session
                </Button>
            </DialogActions>
        </Dialog>
    );
});

export default SessionTimeout;
